import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, withLatestFrom } from 'rxjs';

import { RegistrationType } from 'app/features/manual-input-registration/models';
import { selectAppMode } from 'app/features/settings/state/settings/settings.selectors';
import { ResultDeterminationType } from 'app/shared/models';
import { manualFlowRegistrationDataStorageKey, StorageService } from 'app/shared/services';
import {
    resetSearchParams,
    updateGreenHouseMapFilters,
} from 'app/state/green-house-map/green-house-map.actions';
import { defaultGreenHouseMapFilters } from 'app/state/green-house-map/greenhouse-map.state';
import { getLocationOrActivityState, getLocationOrNextState } from 'app/state/manual-flow/manual-flow.utils';
import { selectLastMultiUserRecentActivityNumberIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';
import { addRegistrationForCurrentRowSelection } from 'app/state/registrations/registrations.actions';
import { resetRowSelection, resumeRowSelection } from 'app/state/row-selection/row-selection.actions';
import {
    selectUserRecentActivityNumberIfLoaded,
    selectUserSessionEmployeeNumber,
} from 'app/state/user-session/user-session.selectors';

import {
    applyManualFlowRegistrationDataFromStorage,
    createRegistrationInManualFlow,
    goBackInManualFlow,
    initManualFlow,
    initManualFlowSuccess,
    manualFlowComponentStateUpdated,
    resetManualFlowRegistrationData,
    setActivityInManualFlow,
    setAmountAndProductBoxInManualFlow,
    setAmountResultInManualFlow,
    setBatchInManualFlow,
    setContainerInManualFlow,
    setEmployeeInManualFlow,
    setLocationInManualFlow,
    setManualFlowComponentState,
    setProductBoxInManualFlow,
    setRowInManualFlow,
    updateManualFlowRegistrationDataInStorage,
} from './manual-flow.actions';
import { emptyManualFlowRegistrationData, ManualFlowRegistrationData } from './manual-flow.reducer';
import {
    selectManualFlowActivity,
    selectManualFlowRegistrationData,
    selectManualInputComponentState,
} from './manual-flow.selectors';
import { getResultDeterminationState, skipEmployeeSelection } from './manual-flow.utils';
import { ComponentState } from './select-state';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ManualFlowEffects {
    monitorStateToUpdateScannedCodesInStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initManualFlowSuccess, applyManualFlowRegistrationDataFromStorage),
            switchMap(() => this.store.select(selectManualFlowRegistrationData)),
            map((registrationData) => updateManualFlowRegistrationDataInStorage(registrationData)),
        ),
    );

    updateManualFlowRegistrationDataInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateManualFlowRegistrationDataInStorage),
                map((registrationData) => {
                    this.storageService.store(manualFlowRegistrationDataStorageKey, registrationData);

                    return true;
                }),
            ),
        { dispatch: false },
    );

    monitorEmployeeNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initManualFlowSuccess, applyManualFlowRegistrationDataFromStorage),
            switchMap(() => this.store.select(selectUserSessionEmployeeNumber)),
            filter((employeeNumber): employeeNumber is number => !!employeeNumber),
            map((userSessionEmployeeNumber) =>
                setEmployeeInManualFlow({ employeeNumber: userSessionEmployeeNumber }),
            ),
        ),
    );

    // This effect is used to monitor the state of the manual flow and update the next component state accordingly
    // It is triggered in manual input component based on either a Tile or Next button is clicked
    monitorNextState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                setActivityInManualFlow,
                setBatchInManualFlow,
                setEmployeeInManualFlow,
                setAmountAndProductBoxInManualFlow,
                setAmountResultInManualFlow,
                setContainerInManualFlow,
                setLocationInManualFlow,
                setProductBoxInManualFlow,
                setRowInManualFlow,
            ),
            withLatestFrom(
                this.store.select(selectManualFlowActivity),
                this.store.select(selectAppMode),
                this.store.select(selectManualInputComponentState),
                this.store.select(selectLastMultiUserRecentActivityNumberIfLoaded),
                this.store.select(selectUserRecentActivityNumberIfLoaded),
            ),
            map(
                ([
                    ,
                    activity,
                    appMode,
                    currentComponentState,
                    recentMultiUserActivityNumber,
                    recentUserActivityNumber,
                ]) => ({
                    activity,
                    appMode,
                    currentComponentState,
                    recentActivityNumber:
                        appMode === 'personal' ? recentUserActivityNumber : recentMultiUserActivityNumber,
                }),
            ),
            switchMap(({ activity, appMode, currentComponentState, recentActivityNumber }) => {
                let nextComponentState = currentComponentState;
                if (!activity) return [];

                // Reset greenhouseMapFilters if activity changes, this prevents the map from showing the wrong data when activity has changed
                if (activity.number !== recentActivityNumber) {
                    this.store.dispatch(
                        updateGreenHouseMapFilters({ greenHouseMapFilters: defaultGreenHouseMapFilters }),
                    );
                }

                switch (currentComponentState) {
                    case ComponentState.SelectActivity:
                        /* After Selecting Activity, we need to check if the activity has a supported location type / result determination type
                         * If there is none, it will check if appMode is single user mode or multi-user mode to show Employee Selection
                         * If all of the above is false, it will go to Create Registration with activity only
                         */
                        if (activity.isBatchInputRequired) nextComponentState = ComponentState.SelectBatch;
                        else nextComponentState = getLocationOrNextState(activity, appMode);
                        break;
                    case ComponentState.SelectBatch:
                        nextComponentState = getLocationOrNextState(activity, appMode);
                        break;
                    case ComponentState.SelectLocation:
                    case ComponentState.SelectRow:
                        nextComponentState = getResultDeterminationState(
                            activity.resultDeterminationType,
                            appMode,
                        );
                        break;
                    case ComponentState.SelectContainers:
                        nextComponentState =
                            activity.resultDeterminationType ===
                            ResultDeterminationType.ByContainerAndProductBox
                                ? ComponentState.SelectProductBox
                                : skipEmployeeSelection(appMode);
                        break;
                    case ComponentState.SelectEmployee:
                        nextComponentState = ComponentState.CreateRegistration;
                        break;
                    default:
                        nextComponentState = skipEmployeeSelection(appMode);
                        break;
                }

                return [setManualFlowComponentState({ componentState: nextComponentState })];
            }),
        ),
    );

    monitorPreviousState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(goBackInManualFlow),
            withLatestFrom(
                this.store.select(selectManualFlowActivity),
                this.store.select(selectManualInputComponentState),
                this.store.select(selectAppMode),
            ),
            switchMap(([, activity, currentComponentState, appMode]) => {
                let previousComponentState = currentComponentState;

                if (!activity) return [];
                switch (currentComponentState) {
                    case ComponentState.SelectBatch:
                        previousComponentState = ComponentState.SelectActivity;
                        break;
                    case ComponentState.SelectLocation:
                    case ComponentState.SelectRow:
                        previousComponentState = activity.isBatchInputRequired
                            ? ComponentState.SelectBatch
                            : ComponentState.SelectActivity;
                        break;
                    /* For all result determination type, when previous state is selected,
                     * go back to either Select Location or Select Activity as no data in between is saved/cached
                     */
                    case ComponentState.SelectAmountAndProductBox:
                    case ComponentState.SelectAmountResult:
                    case ComponentState.SelectContainer:
                    case ComponentState.SelectContainers:
                    case ComponentState.SelectProductBox:
                    case ComponentState.SelectEmployee:
                        if (activity.isBatchInputRequired)
                            previousComponentState = ComponentState.SelectBatch;
                        else {
                            previousComponentState = getLocationOrActivityState(activity);
                            /* Only when previous state is Select Row, we need to resume the row selection
                             * This is done in order to display the manual flow footer in row selection
                             */
                            if (previousComponentState === ComponentState.SelectRow)
                                this.store.dispatch(resumeRowSelection());
                        }
                        break;
                    case ComponentState.CreateRegistration:
                        // By default, user is returned to Select Location or Activity
                        // For batch input, user is able to go back to Select Container or any result determination state
                        previousComponentState = activity.isBatchInputRequired
                            ? getLocationOrNextState(activity, appMode)
                            : getLocationOrActivityState(activity);

                        // However re-using the same function, we might get a Select Employee or Create Registration state
                        // This causes a loop when users are already on the Select Employee page / have created a registration
                        // So we need to reset these states to Select Activity
                        if (
                            [ComponentState.SelectEmployee, ComponentState.CreateRegistration].includes(
                                previousComponentState,
                            )
                        ) {
                            previousComponentState = ComponentState.SelectActivity;
                        }
                        break;
                }

                return [setManualFlowComponentState({ componentState: previousComponentState })];
            }),
        ),
    );

    monitorManualFlowComponentState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setManualFlowComponentState),
            switchMap(({ componentState }) => {
                if (componentState === ComponentState.CreateRegistration) {
                    return [
                        createRegistrationInManualFlow(),
                        goBackInManualFlow(),
                        manualFlowComponentStateUpdated(),
                    ];
                }

                if (componentState === ComponentState.SelectActivity) {
                    return [
                        resetRowSelection(),
                        resetManualFlowRegistrationData(true),
                        manualFlowComponentStateUpdated(),
                    ];
                }

                return [manualFlowComponentStateUpdated()];
            }),
        ),
    );

    createRegistrationInManualFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createRegistrationInManualFlow),
            withLatestFrom(
                this.store.select(selectManualFlowActivity),
                this.store.select(selectManualFlowRegistrationData),
                this.store.select(selectManualInputComponentState),
            ),
            switchMap(([, activity, registrationData]) => {
                if (!activity) return [];
                const amountResult = registrationData.amountResult;
                const batchNumber = registrationData.batchNumber;
                const containers = registrationData.containers;
                const employeeNumberMultiUserMode = registrationData.employeeNumber;
                const productBoxRegistrations = registrationData.productBoxRegistrations;
                const location = registrationData.location;

                return [
                    addRegistrationForCurrentRowSelection({
                        activityNumber: activity.number,
                        amountResult,
                        batchNumber,
                        containers,
                        employeeNumberMultiUserMode,
                        productBoxRegistrations,
                        registrationType: RegistrationType.Labour,
                        location,
                    }),
                    resetRowSelection(),
                    resetSearchParams(),
                    resetManualFlowRegistrationData(),
                ];
            }),
        ),
    );

    onInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initManualFlow),
            withLatestFrom(this.store.select(selectManualInputComponentState)),
            filter(([, state]) => state === ComponentState.CreateRegistration),
            switchMap(() => {
                const partialDataFromStorage = this.storageService.get<Partial<ManualFlowRegistrationData>>(
                    manualFlowRegistrationDataStorageKey,
                );

                const registrationDataFromStorage: ManualFlowRegistrationData | undefined =
                    partialDataFromStorage
                        ? {
                              ...emptyManualFlowRegistrationData,
                              ...partialDataFromStorage,
                          }
                        : undefined;
                this.store.dispatch(
                    applyManualFlowRegistrationDataFromStorage({ registrationDataFromStorage }),
                );
                return [applyManualFlowRegistrationDataFromStorage({ registrationDataFromStorage })];
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private storageService: StorageService,
    ) {}
}
